import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { finalize, forkJoin, map, of, switchMap } from 'rxjs';
import { OrdersService } from '../../core/services/orders.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { CurrentUserService } from '../../core/auth/current-user.service';
import { ProductsService } from '../../core/services/products.service';
import { ProductCategory } from '../../core/models/product.models';
import { leafProductCategories, productCategoryPath } from '../../core/utils/product-category-tree';

type InventoryStatus = 'ready' | 'publish-ready' | 'linked' | 'out' | 'all';

@Component({
  selector: 'app-inventory',
  imports: [RouterLink, FormsModule],
  templateUrl: './inventory.html',
  styleUrls: ['./inventory.scss', './inventory-list.scss', './inventory-list-status.scss'],
})
export class Inventory implements OnInit, OnDestroy, AfterViewChecked {
  private readonly orders = inject(OrdersService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly currentUser = inject(CurrentUserService);
  private readonly products = inject(ProductsService);
  private searchTimer?: ReturnType<typeof setTimeout>;
  private loadObserver?: IntersectionObserver;
  private observedSentinel?: Element;
  @ViewChild('loadSentinel') loadSentinel?: ElementRef<HTMLElement>;
  stock: any[] = [];
  counts = { all: 0, ready: 0, publishReady: 0, linked: 0, outOfStock: 0 };
  activeStatus: InventoryStatus = 'all';
  search = '';
  condition = 'all';
  sort = 'newest';
  page = 1;
  readonly pageSize = 10;
  total = 0;
  hasMore = false;
  isLoading = true;
  isLoadingMore = false;
  errorMessage = '';
  successMessage = '';
  updatingItemId = '';
  auditItem: any | null = null;
  expandedItemId = '';
  categories: ProductCategory[] = [];
  get subcategories(): ProductCategory[] { return leafProductCategories(this.categories); }
  categoryPath(category: ProductCategory): string {
    return productCategoryPath(category, this.categories);
  }
  readonly tabs = [
    { value: 'all' as const, label: 'All inventory', icon: 'inventory_2' },
    { value: 'ready' as const, label: 'Ready to sell', icon: 'point_of_sale' },
    { value: 'publish-ready' as const, label: 'Ready to publish', icon: 'publish' },
    { value: 'linked' as const, label: 'Product linked', icon: 'check_circle' },
    { value: 'out' as const, label: 'Out of stock', icon: 'remove_shopping_cart' },
  ];

  get portalPrefix(): string {
    return this.router.url.startsWith('/admin') ? '/admin' : '/business';
  }
  get canManageInventory(): boolean { return this.currentUser.isBusinessOwner() || ['INVENTORY_CREATE', 'INVENTORY_UPDATE', 'INVENTORY_DELETE', 'INVENTORY_QUANTITY_ADJUST', 'INVENTORY_PUBLISH', 'INVENTORY_UNPUBLISH'].some(permission => this.currentUser.hasPermission(permission)); }
  get canRecordPurchase(): boolean { return ['PURCHASE_CREATE', 'PROCUREMENT_COMPANY_USE', 'PROCUREMENT_NEW_PRODUCT', 'PROCUREMENT_USED_PRODUCT'].some(permission => this.currentUser.hasPermission(permission)); }
  get totalUnits(): number {
    return this.stock.reduce((total, item) => total + Number(item.quantity || 0), 0);
  }
  get availableUnits(): number {
    return this.stock.reduce(
      (total, item) => total + Number(item.availableQuantity ?? item.quantity ?? 0),
      0,
    );
  }
  ngOnInit(): void {
    this.loadInventory(true);
    this.loadProductCategories().subscribe({
      next: values => this.categories = values,
      error: () => this.categories = [],
    });
  }

  private loadProductCategories() {
    return this.products.categories().pipe(
      map(values => values.filter((value): value is ProductCategory => 'id' in value && Boolean(value.id))),
      switchMap(roots => !roots.length
        ? of(roots)
        : forkJoin(roots.map(root => this.products.childCategories(root.id))).pipe(
            map(groups => groups.flat()),
            switchMap(children => !children.length
              ? of([...roots])
              : forkJoin(children.map(child => this.products.childCategories(child.id))).pipe(
                  map(groups => [...roots, ...children, ...groups.flat()]),
                )),
          )),
    );
  }
  ngAfterViewChecked(): void {
    const sentinel = this.loadSentinel?.nativeElement;
    if (!sentinel || sentinel === this.observedSentinel) return;

    this.loadObserver?.disconnect();
    this.observedSentinel = sentinel;
    this.loadObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadInventory(false);
      },
      { rootMargin: '250px' },
    );
    this.loadObserver.observe(sentinel);
  }
  ngOnDestroy(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.loadObserver?.disconnect();
  }

  selectTab(status: InventoryStatus): void {
    if (status === this.activeStatus) return;
    this.activeStatus = status;
    this.loadInventory(true);
  }
  onSearch(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadInventory(true), 350);
  }
  applyFilters(): void {
    this.loadInventory(true);
  }
  clearFilters(): void {
    this.search = '';
    this.condition = 'all';
    this.sort = 'newest';
    this.loadInventory(true);
  }
  tabCount(status: InventoryStatus): number {
    if (status === 'out') return this.counts.outOfStock;
    if (status === 'publish-ready') return this.counts.publishReady;
    return this.counts[status];
  }

  loadInventory(reset = true): void {
    if (reset) {
      this.page = 1;
      this.stock = [];
      this.isLoading = true;
    } else {
      if (!this.hasMore || this.isLoadingMore) return;
      this.page += 1;
      this.isLoadingMore = true;
    }
    this.errorMessage = '';
    this.orders
      .inventory({
        status: this.activeStatus,
        search: this.search.trim() || undefined,
        condition: this.condition,
        sort: this.sort,
        page: this.page,
        pageSize: this.pageSize,
      })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.isLoadingMore = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const items = (response?.items ?? []).map((item: any) => ({
            ...item,
            imageUrls: this.parseImages(item.imageUrlsJson ?? item.imageUrls ?? item.images),
          }));
          this.stock = reset ? items : [...this.stock, ...items];
          this.counts = response?.counts ?? this.counts;
          this.total = Number(response?.total ?? this.stock.length);
          this.hasMore = Boolean(response?.hasMore);
          this.cdr.detectChanges();
        },
        error: (error) => {
          if (!reset) this.page = Math.max(1, this.page - 1);
          this.errorMessage = httpErrorMessage(error, 'Unable to load purchased inventory.');
          this.cdr.detectChanges();
        },
      });
  }
  createListing(item: any): void {
    const verificationUrls = this.parseImages(item.verificationImageUrlsJson);
    const invoiceUrls = this.parseImages(item.invoiceUrlsJson);
    void this.router.navigate([`${this.portalPrefix}/products/create`], {
      queryParams: {
        purchaseItemId: item.id,
        title: item.title,
        modelName: item.modelName,
        condition: item.condition,
        cost: item.unitCost,
        repairCost: item.repairCost,
        extraCost: item.extraCost,
        storeId: item.purchase.storeId,
        sellerUserId: item.purchase.individualSellerUserId,
        existingSellerVerificationImageUrl: verificationUrls[0] ?? '',
        existingOriginalBillUrl: invoiceUrls[0] ?? '',
        existingImageUrlsJson: JSON.stringify(item.imageUrls ?? []),
      },
    });
  }
  openAudit(item: any): void { this.auditItem = item; }
  closeAudit(): void { this.auditItem = null; }
  toggleManage(item: any): void { this.expandedItemId = this.expandedItemId === item.id ? '' : item.id; }
  evidenceUrls(value?: unknown): string[] { return this.parseImages(value); }
  toggleSaleReadiness(item: any): void {
    const enabled = !item.internalSaleEnabled;
    if (enabled && !item.categoryId) { this.errorMessage = 'Select a category so the configured profit rule can be applied.'; return; }
    this.updatingItemId = item.id;
    this.errorMessage = '';
    this.successMessage = '';
    this.orders.setInventorySaleReadiness(item.id, enabled, Number(item.maxDiscountPercent || 0), item.categoryId).pipe(finalize(() => {
      this.updatingItemId = '';
      this.cdr.detectChanges();
    })).subscribe({
      next: (result) => {
        item.internalSaleEnabled = enabled;
        if (result) { item.sellingPrice = result.sellingPrice; item.profitPercentage = result.profitPercentage; }
        this.successMessage = enabled
          ? `${item.title} is now available for internal sale.`
          : `${item.title} was removed from internal sale.`;
        this.loadInventory(true);
      },
      error: (error) => this.errorMessage = httpErrorMessage(error, 'Unable to update sale readiness.'),
    });
  }
  saveDiscountLimit(item: any): void {
    const limit = Math.max(0, Math.min(100, Number(item.maxDiscountPercent) || 0));
    item.maxDiscountPercent = limit;
    this.updatingItemId = item.id;
    this.orders.setInventorySaleReadiness(item.id, Boolean(item.internalSaleEnabled), limit, item.categoryId)
      .pipe(finalize(() => { this.updatingItemId = ''; this.cdr.detectChanges(); }))
      .subscribe({
        next: () => this.successMessage = `${item.title} discount limit updated to ${limit}%.`,
        error: (error) => this.errorMessage = httpErrorMessage(error, 'Unable to update discount limit.'),
      });
  }
  money(value: number): string {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(
      value || 0,
    );
  }
  landedCost(item: any): number { return Number(item.unitCost || 0) + Number(item.repairCost || 0) + Number(item.extraCost || 0); }
  sellingPrice(item: any): number { return Number(item.sellingPrice || 0); }
  unitProfit(item: any): number { return this.sellingPrice(item) - this.landedCost(item); }
  purchaseDate(value?: string): string {
    return value
      ? new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' }).format(new Date(value))
      : 'Date unavailable';
  }
  private parseImages(value?: unknown): string[] {
    if (Array.isArray(value))
      return value.filter((x): x is string => typeof x === 'string' && Boolean(x));
    if (typeof value !== 'string' || !value.trim()) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed.filter((x): x is string => typeof x === 'string' && Boolean(x))
        : [];
    } catch {
      return [];
    }
  }
}
